import { ModuleId, VisualAidType } from '../types'

export interface HintVisual {
  type: VisualAidType
  data: Record<string, unknown>
}

export interface HintStep {
  title?: string
  content: string
  visual?: HintVisual
}

export interface SubSkillHint {
  id: string
  title: string
  description?: string
  steps: HintStep[]
  relatedSubSkills?: string[]
}

export type HintRegistry = Record<ModuleId, Record<string, SubSkillHint>>

/**
 * HINT REGISTRY v3.0
 *
 * Keys MUST match the actual subSkill values used in question JSON files.
 * This ensures getHintsForSubSkill() returns targeted hints during practice.
 *
 * COMPLETE SubSkill inventory per module (from full question scan):
 *
 * Metriek (G6-G8):
 *   lengte, lengte_km, lengte_km_m, gewicht, gewicht_kg, inhoud,
 *   inhoud_schatten, vergelijken, oppervlakte, oppervlakte_driehoek,
 *   oppervlakte_omrekenen, omtrek, omtrek_terug
 *
 * Getalbegrip (G6-G8):
 *   positiewaarde, breuken, breuken_vergelijken, breuken_midden,
 *   aftrekken, optellen, vermenigvuldigen, getallen_vergelijken,
 *   verhoudingen, procenten, procenten_sommen, procenten_meer,
 *   kommagetallen, gemiddelde, reeksen, deelbaarheid, schatten,
 *   meerstaps, delen_klein, rekenvolgorde
 *
 * TijdGeld (G6-G8):
 *   Skills: klokkijken, tijdrekenen, kalender, geld_tellen, geld_rekenen, snelheid, grafieken, financieel
 *   SubSkills:
 *     klokkijken: klokkijken_basis, klokkijken_digitaal, klokkijken_seconden
 *     tijdrekenen: tijdsduur_berekenen, tijd_omrekenen, tijdzones
 *     kalender: kalender_basis, kalender_schrikkel
 *     geld_tellen: geld_munten, geld_wisselgeld
 *     geld_rekenen: boodschappen, prijzen_vergelijken, korting, sparen
 *     snelheid: snelheid_berekenen
 *     grafieken: tabellen_lezen, grafieken_lezen, patronen
 *     financieel: rente, loon, valuta, btw
 *
 * SpellingWW (G6-G8):
 *   Skills: stam_herkennen, tegenwoordige_tijd, verleden_tijd,
 *           voltooid_deelwoord, bijvoeglijk_deelwoord, werkwoord_extra
 *   SubSkills:
 *     stam_herkennen: stam
 *     tegenwoordige_tijd: tt_ik, tt_hij, tt_jij, tt_jij_inversie, tt_wij, tt_dt
 *     verleden_tijd: vt_kofschip, vt_geen_kofschip, vt_sterk, vt_lastig
 *     voltooid_deelwoord: vd_kofschip, vd_geen_kofschip, vd_sterk, vd_voorvoegsel
 *     bijvoeglijk_deelwoord: bn_vd
 *     werkwoord_extra: homofonen, gebiedende_wijs
 *
 * SpellingRegel (G6-G8):
 *   Skills: klanken_spelling, weetwoorden, schrijfwijze, zinsontleding, woordsoorten, leestekens
 *   SubSkills:
 *     klanken_spelling: lange_klank, korte_klank, v_f_wissel, s_z_wissel, eind_d_t, algemeen
 *     weetwoorden: weetwoorden_eau, weetwoorden_accent, weetwoorden_c, weetwoorden_tie_age, weetwoorden_frans, weetwoorden_moeilijk
 *     schrijfwijze: trema, meervoud, s_genitief, hoofdletter, tussen_n_s, samenstelling
 *     zinsontleding: onderwerp, persoonsvorm, lijdend_voorwerp, meewerkend_voorwerp
 *     woordsoorten: woordsoorten, voorzetsels, voegwoorden
 *     leestekens: leestekens
 *
 * Taalbegrip (G6-G8):
 *   Skills: tekstbegrip, woordenschat, tekstanalyse, verbanden, kritisch_lezen, stijl
 *   SubSkills:
 *     tekstbegrip: letterlijk_begrip, informatief_lezen, volgorde
 *     woordenschat: woordbetekenis, spreekwoorden, taalbegrippen
 *     tekstanalyse: hoofdgedachte, conclusie_trekken, doel_schrijver, tekststructuur, tekstsoort
 *     verbanden: verwijswoorden, signaalwoorden, oorzaak_gevolg
 *     kritisch_lezen: feit_mening, perspectief, samenvatten, argumentatie
 *     stijl: stijlfiguren
 *
 * Topo: nederland, europa, wereld (coming soon)
 * Studievaardig: opzoeken, etc. (coming soon)
 */

export const HINT_REGISTRY: HintRegistry = {
  // ═══════════════════════════════════════════
  // METRIEK
  // ═══════════════════════════════════════════
  Metriek: {
    lengte: {
      id: 'metriek-lengte',
      title: '📐 Lengtematen Omrekenen',
      description: 'Van kilometer naar millimeter en terug.',
      steps: [
        {
          content:
            'Gebruik het metriek trapje: km → hm → dam → m → dm → cm → mm',
          visual: {
            type: 'stairs',
            data: { type: 'length', showAnimation: true },
          },
        },
        {
          title: 'Naar beneden = ×10',
          content:
            'Elke stap naar beneden vermenigvuldig je met 10 (een nul erbij).\nVoorbeeld: 3 m = 30 dm = 300 cm = 3.000 mm',
        },
        {
          title: 'Naar boven = ÷10',
          content:
            'Elke stap naar boven deel je door 10 (een nul eraf).\nVoorbeeld: 5.000 mm = 500 cm = 50 dm = 5 m',
        },
      ],
    },
    lengte_km: {
      id: 'metriek-lengte-km',
      title: '🛣️ Kilometers en Meters',
      description: 'Omrekenen tussen km en m.',
      steps: [
        {
          title: 'Basisregel',
          content:
            '1 km = 1.000 meter\nKilo betekent duizend!\n\nVoorbeeld: 3 km = 3 × 1.000 = 3.000 meter',
        },
        {
          title: 'Terug naar km',
          content:
            'Van meter naar km: deel door 1.000.\n5.000 m = 5.000 : 1.000 = 5 km',
        },
      ],
    },
    lengte_km_m: {
      id: 'metriek-lengte-km-m',
      title: '🛣️ Kilometers met Komma',
      description: 'Omrekenen van km met decimalen naar meters.',
      steps: [
        {
          title: 'Stappenplan',
          content:
            'Vermenigvuldig met 1.000.\n3,2 km = 3,2 × 1.000 = 3.200 m\n\nTip: de komma schuift 3 plekken naar rechts.',
        },
        {
          title: 'Andersom',
          content:
            'Van meter naar km: deel door 1.000 (komma 3 plekken naar links).\n4.500 m = 4,5 km',
        },
      ],
    },
    gewicht: {
      id: 'metriek-gewicht',
      title: '⚖️ Gewichtsmaten',
      description: 'Rekenen met kilogram en gram.',
      steps: [
        {
          content:
            'Van kilogram naar gram is 3 stappen naar beneden (×1000).\nVan gram naar kilogram is 3 stappen naar boven (÷1000).',
          visual: {
            type: 'stairs',
            data: { type: 'weight', showAnimation: true },
          },
        },
        {
          title: 'Handige weetjes',
          content:
            '1 kg = 1.000 gram\n1 ton = 1.000 kg\nEen pak suiker = 1 kg\nEen appel ≈ 200 gram',
        },
      ],
    },
    gewicht_kg: {
      id: 'metriek-gewicht-kg',
      title: '⚖️ Gram naar Kilogram',
      description: 'Omrekenen van gram naar kg en andersom.',
      steps: [
        {
          title: 'Basisregel',
          content:
            '1 kg = 1.000 gram\nGram naar kg: deel door 1.000.\n4.500 g = 4.500 : 1.000 = 4,5 kg',
        },
        {
          title: 'Met kommagetallen',
          content:
            '2,5 kg = 2.500 gram\n0,75 kg = 750 gram\nTip: de komma schuift 3 plekken.',
        },
      ],
    },
    inhoud: {
      id: 'metriek-inhoud',
      title: '🧃 Inhoudsmaten (Liters)',
      description: 'Rekenen met liters, deciliters en milliliters.',
      steps: [
        {
          content:
            'Het metriek stelsel voor inhoud werkt hetzelfde als voor lengte.',
          visual: {
            type: 'stairs',
            data: { type: 'volume', showAnimation: true },
          },
        },
        {
          title: 'Kubieke Meters',
          content:
            '1 m³ (een kuub) = 1.000 liter\n1 dm³ = 1 liter\n1 cm³ = 1 ml',
          visual: {
            type: 'rule',
            data: {
              title: 'Weetje',
              content: '1 dm³ = 1 liter. Dus inhoud in dm³ = inhoud in liters!',
              icon: 'droplet',
            },
          },
        },
        {
          title: 'Inhoud berekenen',
          content:
            'Inhoud = lengte × breedte × hoogte\nLet op: zet eerst alle maten om naar dezelfde eenheid!',
        },
      ],
    },
    inhoud_schatten: {
      id: 'metriek-inhoud-schatten',
      title: '🧃 Inhoud Schatten',
      description: 'Hoeveel past erin? Schatten van inhoud.',
      steps: [
        {
          title: 'Handige referenties',
          content:
            'Een glas = ± 200 ml\nEen pak melk = 1 liter\nEen emmer = ± 10 liter\nEen bad = ± 150 liter\nEen regenton = ± 100 liter',
        },
        {
          title: 'Tip',
          content:
            'Vergelijk altijd met iets dat je kent.\n"Past het in een glas? Dan is het minder dan 200 ml."',
        },
      ],
    },
    vergelijken: {
      id: 'metriek-vergelijken',
      title: '⚖️ Maten Vergelijken',
      description: 'Welke maat is groter?',
      steps: [
        {
          title: 'Strategie',
          content:
            'Zet beide maten om naar dezelfde eenheid.\nDan kun je ze vergelijken!\n\nVoorbeeld: 2 kg of 1.500 gram?\n2 kg = 2.000 gram → 2.000 > 1.500 → 2 kg is zwaarder.',
        },
        {
          title: 'Veelgemaakte fout',
          content:
            'Vergelijk nooit getallen zonder naar de eenheid te kijken!\n150 cm is MEER dan 1 meter (want 1 m = 100 cm).',
        },
      ],
    },
    oppervlakte: {
      id: 'metriek-oppervlakte',
      title: '⬛ Oppervlakte',
      description: 'Hoeveel er op een vlak past.',
      steps: [
        {
          title: 'Formule',
          content:
            'Oppervlakte = lengte × breedte\nBij een vierkant: zijde × zijde',
        },
        {
          title: 'Terugrekenen',
          content:
            'Als je de oppervlakte weet en één zijde:\nlengte = oppervlakte ÷ breedte',
        },
        {
          title: 'Landmaten',
          content:
            '1 are = 100 m²\n1 hectare = 10.000 m²\n1 km² = 1.000.000 m²',
        },
      ],
    },
    oppervlakte_driehoek: {
      id: 'metriek-oppervlakte-driehoek',
      title: '🔺 Oppervlakte Driehoek',
      description: 'De oppervlakte van een driehoek berekenen.',
      steps: [
        {
          title: 'Formule',
          content:
            'Oppervlakte driehoek = (basis × hoogte) : 2\n\nVoorbeeld: basis 6 cm, hoogte 4 cm\n(6 × 4) : 2 = 24 : 2 = 12 cm²',
        },
        {
          title: 'Waarom delen door 2?',
          content:
            'Een driehoek is altijd de helft van een rechthoek.\nDus: eerst de rechthoek berekenen, dan halveren.',
        },
      ],
    },
    oppervlakte_omrekenen: {
      id: 'metriek-oppervlakte-omrekenen',
      title: '⬛ Oppervaktematen Omrekenen',
      description: 'Van cm² naar dm² naar m² en terug.',
      steps: [
        {
          title: 'Belangrijk verschil!',
          content:
            'Bij oppervlakte gaat elke stap met ×100 (niet ×10!).\n1 dm² = 100 cm²\n1 m² = 100 dm² = 10.000 cm²\n1 km² = 1.000.000 m²',
        },
        {
          title: 'Waarom ×100?',
          content:
            'Oppervlakte is lengte × breedte.\nBeide worden ×10, dus samen ×10 × ×10 = ×100.',
        },
      ],
    },
    omtrek: {
      id: 'metriek-omtrek',
      title: '🔄 Omtrek',
      description: 'De afstand eromheen.',
      steps: [
        {
          title: 'Formule',
          content:
            'Omtrek = 2 × (lengte + breedte)\nBij een vierkant: 4 × zijde',
        },
        {
          title: 'Terugrekenen',
          content:
            'Trek 2 × breedte af van de omtrek.\nDeel het restant door 2 = de lengte.',
        },
        {
          content:
            'Omtrek = hek (eromheen lopen). Denk aan een hek om een tuin.',
          visual: {
            type: 'rule',
            data: {
              title: 'Ezelsbruggetje',
              content: 'Omtrek = hek om de tuin',
              icon: 'lightbulb',
            },
          },
        },
      ],
    },
    omtrek_terug: {
      id: 'metriek-omtrek-terug',
      title: '🔄 Omtrek Terugrekenen',
      description: 'Een ontbrekende zijde berekenen uit de omtrek.',
      steps: [
        {
          title: 'Stappenplan',
          content:
            'Omtrek = 2 × lengte + 2 × breedte\n\nStap 1: Trek 2 × de bekende zijde af van de omtrek\nStap 2: Deel het resultaat door 2\n\nVoorbeeld: Omtrek = 30, lengte = 10\n30 - (2 × 10) = 10\n10 : 2 = 5 → breedte = 5 cm',
        },
      ],
    },
  },

  // ═══════════════════════════════════════════
  // GETALBEGRIP
  // ═══════════════════════════════════════════
  Getalbegrip: {
    positiewaarde: {
      id: 'getalbegrip-positiewaarde',
      title: '#️⃣ Positiewaarde',
      description: 'De waarde van een cijfer in een getal.',
      steps: [
        {
          content:
            'Kijk goed naar de plek van het cijfer:\nTD - D - H - T - E\n(Tienduizendtallen - Duizendtallen - Honderdtallen - Tientallen - Eenheden)',
        },
        {
          title: 'Voorbeeld: 78.432',
          content:
            '7 = 70.000 (tienduizendtallen)\n8 = 8.000 (duizendtallen)\n4 = 400 (honderdtallen)\n3 = 30 (tientallen)\n2 = 2 (eenheden)',
        },
      ],
    },
    aftrekken: {
      id: 'getalbegrip-aftrekken',
      title: '➖ Kolomsgewijs Aftrekken',
      description: 'Stap voor stap van elkaar afhalen.',
      steps: [
        {
          title: 'Stap 1: Begin rechts',
          content:
            'Begin altijd bij de eenheden (helemaal rechts). Daarna de tientallen, dan honderdtallen.',
        },
        {
          title: 'Stap 2: Lenen als het moet',
          content:
            'Is het bovenste getal kleiner? Leen 1 van de buurman (die is 10 waard!).',
        },
      ],
    },
    optellen: {
      id: 'getalbegrip-optellen',
      title: '➕ Kolomsgewijs Optellen',
      description: 'Stap voor stap bij elkaar optellen.',
      steps: [
        {
          title: 'Stap 1: Begin rechts',
          content: 'Begin bij de eenheden. Tel de cijfers in elke kolom op.',
        },
        {
          title: 'Stap 2: Onthouden',
          content:
            'Is de uitkomst 10 of meer? Schrijf het laatste cijfer op en onthoud 1 voor de volgende kolom.',
        },
      ],
    },
    vermenigvuldigen: {
      id: 'getalbegrip-vermenigvuldigen',
      title: '✖️ Vermenigvuldigen',
      description: 'Keer-sommen uitrekenen.',
      steps: [
        {
          title: 'Handig splitsen',
          content:
            '24 × 9 = 24 × 10 − 24 = 240 − 24 = 216\n15 × 12 = 15 × 10 + 15 × 2 = 150 + 30 = 180',
        },
        {
          title: 'Tafels kennen',
          content:
            'Ken je tafels van 1 t/m 10 goed!\nTip: de tafel van 9 → het tiental stijgt, het eental daalt.',
        },
      ],
    },
    breuken: {
      id: 'getalbegrip-breuken',
      title: '🍕 Alles over Breuken',
      description: 'Teller, noemer en rekenen met breuken.',
      steps: [
        {
          title: 'Teller en Noemer',
          content:
            'De **teller** (boven) is hoeveel stukken je hebt.\nDe **noemer** (onder) is het totaal aantal stukken.',
          visual: {
            type: 'fraction',
            data: {
              numerator: 3,
              denominator: 4,
              label: 'Driekwart (3/4)',
              visualType: 'pizza',
              usePizza: true,
            },
          },
        },
        {
          title: 'Breuken Optellen',
          content:
            'Bij gelijke noemers: tel alleen de tellers op! De noemer blijft gelijk.\nVoorbeeld: 1/4 + 2/4 = 3/4',
        },
        {
          title: 'Deel van een geheel',
          content: '1/3 van 12 = 12 ÷ 3 = 4\n2/5 van 20 = (20 ÷ 5) × 2 = 8',
        },
        {
          title: 'Vereenvoudigen',
          content:
            'Deel teller en noemer door hetzelfde getal.\n12/60 → beide ÷12 → 1/5',
        },
      ],
    },
    breuken_vergelijken: {
      id: 'getalbegrip-breuken-vergelijken',
      title: '⚖️ Breuken Vergelijken',
      description: 'Welke breuk is groter?',
      steps: [
        {
          title: 'Zelfde noemer (onderkant)?',
          content:
            'Kijk naar de teller! Grotere teller = grotere breuk.\n3/5 > 2/5',
        },
        {
          title: 'Zelfde teller (bovenkant)?',
          content:
            'Kleinere noemer = grotere stukken = grotere breuk!\n1/3 > 1/4 (want derde stukken zijn groter dan kwart stukken)',
        },
      ],
    },
    breuken_midden: {
      id: 'getalbegrip-breuken-midden',
      title: '🍕 Breuk Tussen Twee Breuken',
      description:
        'Een breuk vinden die precies tussen twee andere breuken ligt.',
      steps: [
        {
          title: 'Stappenplan',
          content:
            'Stap 1: Maak de breuken gelijknamig (zelfde noemer)\nStap 2: Kijk welk getal tussen de twee tellers ligt\n\nVoorbeeld: Tussen 1/3 en 1/2\n1/3 = 4/12, 1/2 = 6/12\nTussen 4 en 6 ligt 5 → 5/12',
        },
        {
          title: 'Tip',
          content:
            'Lukt het niet? Maak de noemer groter!\nVermenigvuldig beide noemers met 2 of 3 om meer ruimte te krijgen.',
        },
      ],
    },
    getallen_vergelijken: {
      id: 'getalbegrip-getallen-vergelijken',
      title: '🔢 Getallen Vergelijken',
      description: 'Welk getal is het grootst?',
      steps: [
        {
          content:
            'Vergelijk van links naar rechts:\n1. Eerst de duizendtallen\n2. Dan de honderdtallen\n3. Dan de tientallen\n4. Dan de eenheden\n\nHet eerste verschil bepaalt welk getal groter is.',
        },
      ],
    },
    procenten: {
      id: 'getalbegrip-procenten',
      title: '📊 Percentages',
      description: 'Handige percentages om te weten.',
      steps: [
        {
          content:
            '50% = de helft (÷2)\n25% = een kwart (÷4)\n75% = driekwart\n10% = een tiende (÷10)\n1% = een honderdste (÷100)',
        },
        {
          title: 'Percentage berekenen',
          content:
            'Stap 1: Bereken het verschil\nStap 2: Deel door het oorspronkelijke bedrag\nStap 3: Vermenigvuldig met 100\n\nVoorbeeld: van €320 naar €368\nVerschil: 48. Percentage: 48 ÷ 320 = 0,15 = 15%',
        },
        {
          title: 'Terugrekenen bij korting',
          content:
            '8% korting? Dan betaal je 92% (100% - 8%).\nOude prijs = nieuwe prijs ÷ 0,92',
        },
      ],
    },
    procenten_sommen: {
      id: 'getalbegrip-procenten-sommen',
      title: '📊 Procentensommen',
      description: 'Percentage van een getal berekenen in contextvragen.',
      steps: [
        {
          title: 'Stappenplan',
          content:
            'Stap 1: Bereken 10% (deel door 10)\nStap 2: Gebruik 10% om het gevraagde percentage te berekenen\n\nVoorbeeld: 40% van 25\n10% = 2,5 → 40% = 4 × 2,5 = 10',
        },
        {
          title: 'Alternatief: breuk gebruiken',
          content:
            '40% = 2/5\n1/5 van 25 = 5\n2/5 van 25 = 10\n\nTip: 25%, 50%, 75% zijn makkelijk via breuken (1/4, 1/2, 3/4).',
        },
      ],
    },
    procenten_meer: {
      id: 'getalbegrip-procenten-meer',
      title: '📊 Meer dan 100%',
      description: 'Percentages boven de 100% berekenen.',
      steps: [
        {
          title: 'Hoe werkt het?',
          content:
            '100% = het hele getal\n125% = het hele getal + 25% extra\n200% = het dubbele\n\nVoorbeeld: 125% van 80\n100% = 80, 25% = 20 → 80 + 20 = 100',
        },
        {
          title: 'Snelle methode',
          content:
            'Zet het percentage om naar een decimaal getal:\n125% = 1,25\n150% = 1,50\n200% = 2,00\n\nDan: getal × decimaal = antwoord\n80 × 1,25 = 100',
        },
      ],
    },
    delen_klein: {
      id: 'getalbegrip-delen-klein',
      title: '➗ Delen door Kleine Getallen',
      description: 'Delen door 0,1 of 0,01 enz.',
      steps: [
        {
          title: 'De truc',
          content:
            'Delen door een klein getal maakt het antwoord GROTER!\n\nDelen door 0,1 = vermenigvuldigen met 10\nDelen door 0,01 = vermenigvuldigen met 100\nDelen door 0,001 = vermenigvuldigen met 1.000',
        },
        {
          title: 'Voorbeeld',
          content:
            '1000 : 0,01 = 1000 × 100 = 100.000\n\nTip: tel het aantal nullen achter de komma.\n0,01 heeft 2 cijfers → schuif de komma 2 plekken naar rechts.',
        },
      ],
    },
    rekenvolgorde: {
      id: 'getalbegrip-rekenvolgorde',
      title: '🔢 Rekenvolgorde',
      description: 'In welke volgorde reken je?',
      steps: [
        {
          title: 'De regels',
          content:
            '1. Eerst haakjes ( )\n2. Dan machtsverheffen\n3. Dan vermenigvuldigen (×) en delen (:)\n4. Als laatste optellen (+) en aftrekken (−)\n\nEzelsbruggetje: "Hoe Moeilijk Van De Ene Op De Andere"',
        },
        {
          title: 'Voorbeeld',
          content:
            '3 + 4 × 5 − 2 =\nEerst: 4 × 5 = 20\nDan: 3 + 20 − 2 = 21\n\nNIET: (3 + 4) × 5 − 2 = 33 ← FOUT!',
        },
      ],
    },
    verhoudingen: {
      id: 'getalbegrip-verhoudingen',
      title: '⚖️ Verhoudingen',
      description: 'Rekenen met verhoudingen en schaal.',
      steps: [
        {
          title: 'Stappenplan',
          content:
            'Stap 1: Tel de delen op (bijv. 2:3 = 5 delen)\nStap 2: Deel het totaal door het aantal delen\nStap 3: Vermenigvuldig met het gewenste deel',
        },
        {
          title: 'Voorbeeld',
          content:
            'Verhouding rood:blauw = 2:3, totaal 750 ml\n5 delen → 1 deel = 150 ml\nRood = 2 × 150 = 300 ml',
        },
        {
          title: 'Schaal',
          content:
            'Schaal 1:25.000 betekent: 1 cm op de kaart = 25.000 cm in het echt = 250 m.\nVermenigvuldig de kaartafstand met het schaalgetal.',
        },
      ],
    },
    kommagetallen: {
      id: 'getalbegrip-kommagetallen',
      title: '🔢 Kommagetallen',
      description: 'Rekenen met decimale getallen.',
      steps: [
        {
          title: 'Vermenigvuldigen',
          content:
            'Reken zonder komma, tel daarna het totaal aantal decimalen.\n0,2 × 0,3: 2 × 3 = 6, twee decimalen → 0,06',
        },
        {
          title: 'Delen',
          content:
            'Maak de deler een heel getal door beide met 10 (of 100) te vermenigvuldigen.\n0,6 : 0,2 → 6 : 2 = 3',
        },
      ],
    },
    gemiddelde: {
      id: 'getalbegrip-gemiddelde',
      title: '📈 Gemiddelde Berekenen',
      description: 'Het gemiddelde van een reeks getallen.',
      steps: [
        {
          title: 'Stappenplan',
          content:
            'Stap 1: Tel alle getallen bij elkaar op\nStap 2: Deel door het AANTAL getallen\n\nVoorbeeld: 6, 8, 10\nSom: 24. Aantal: 3. Gemiddelde: 24 ÷ 3 = 8',
        },
      ],
    },
    reeksen: {
      id: 'getalbegrip-reeksen',
      title: '🔗 Getallenreeksen',
      description: 'Het patroon ontdekken en doorzetten.',
      steps: [
        {
          title: 'Stappenplan',
          content:
            'Stap 1: Kijk naar het verschil tussen opeenvolgende getallen\nStap 2: Is het verschil steeds hetzelfde? (+4, +4, +4)\nStap 3: Of verdubbelt het? (×2, ×2, ×2)\nStap 4: Pas het patroon toe op het laatste getal',
        },
        {
          title: 'Voorbeeld',
          content: '2, 4, 8, 16, ... → steeds ×2 → 16 × 2 = 32',
        },
      ],
    },
    deelbaarheid: {
      id: 'getalbegrip-deelbaarheid',
      title: '➗ Deelbaarheid',
      description: 'Wanneer is een getal deelbaar?',
      steps: [
        {
          title: 'Deelbaarheidsregels',
          content:
            'Deelbaar door 2: eindigt op even getal (0, 2, 4, 6, 8)\nDeelbaar door 3: som van cijfers deelbaar door 3\nDeelbaar door 5: eindigt op 0 of 5\nDeelbaar door 9: som van cijfers deelbaar door 9\nDeelbaar door 10: eindigt op 0',
        },
        {
          title: 'Voorbeeld',
          content: '123: 1+2+3 = 6. 6 is deelbaar door 3, dus 123 ook!',
        },
      ],
    },
    schatten: {
      id: 'getalbegrip-schatten',
      title: '🎯 Schatten & Afronden',
      description: 'Snel een schatting maken.',
      steps: [
        {
          title: 'Strategie',
          content:
            'Rond de getallen af naar makkelijke getallen.\n49,8 → 50\n20,1 → 20\nDan: 50 × 20 = 1000',
        },
        {
          title: 'Wanneer schatten?',
          content:
            'Bij "schat de uitkomst" of "ongeveer hoeveel" hoef je niet precies te rekenen.\nKies het antwoord dat het dichtst bij je schatting ligt.',
        },
      ],
    },
    meerstaps: {
      id: 'getalbegrip-meerstaps',
      title: '🧩 Meerstapsproblemen',
      description: 'Problemen met meerdere rekenstappen.',
      steps: [
        {
          content:
            'Stap 1: Lees de vraag goed en zoek de getallen\nStap 2: Bedenk welke bewerking(en) je nodig hebt\nStap 3: Reken stap voor stap uit\nStap 4: Controleer je antwoord',
        },
      ],
    },
  },

  // ═══════════════════════════════════════════
  // TIJD & GELD
  // ═══════════════════════════════════════════
  TijdGeld: {
    // ── KLOKKIJKEN ──
    klokkijken_basis: {
      id: 'tijdgeld-klokkijken-basis',
      title: '🕐 Klokkijken Basis',
      description: 'Analoog en digitaal klokkijken.',
      steps: [
        {
          content:
            'Kleine wijzer = uren\nGrote wijzer = minuten\n\n1 uur = 60 minuten',
        },
        {
          title: 'Kwartieren',
          content:
            'Kwart over = 15 minuten na het hele uur\nHalf = 30 minuten na het hele uur\nKwart voor = 15 minuten voor het volgende uur',
        },
      ],
    },
    klokkijken_digitaal: {
      id: 'tijdgeld-klokkijken-digitaal',
      title: '🕐 Digitale Klok',
      description: 'De digitale klok lezen.',
      steps: [
        {
          content:
            "De digitale klok toont: UU:MM\nUU = uren (0-23)\nMM = minuten (0-59)\n\n13:00 = 1 uur 's middags\n14:30 = half drie\n00:00 = middernacht",
        },
      ],
    },
    klokkijken_seconden: {
      id: 'tijdgeld-klokkijken-seconden',
      title: '⏱️ Klokkijken met Seconden',
      description: 'Klokkijken tot op de seconde.',
      steps: [
        {
          content:
            'De secondewijzer is de dunne wijzer.\nBij de 3 = 15 seconden\nBij de 6 = 30 seconden\nBij de 9 = 45 seconden\nBij de 12 = 0 seconden',
        },
        {
          title: 'Digitaal formaat',
          content:
            'UU:MM:SS (uren:minuten:seconden)\nDe laatste twee cijfers zijn altijd de seconden.',
        },
      ],
    },

    // ── TIJDREKENEN ──
    tijdsduur_berekenen: {
      id: 'tijdgeld-tijdsduur-berekenen',
      title: '⏳ Tijdsduur Berekenen',
      description: 'Hoelang duurt iets?',
      steps: [
        {
          title: 'Stappenplan',
          content:
            'Stap 1: Tel eerst naar het volgende hele uur\nStap 2: Tel dan de hele uren\nStap 3: Tel de resterende minuten',
        },
        {
          title: 'Voorbeeld: 14:30 tot 16:45',
          content:
            '14:30 → 16:30 = 2 uur\n16:30 → 16:45 = 15 minuten\nTotaal: 2 uur en 15 minuten',
        },
      ],
    },
    tijd_omrekenen: {
      id: 'tijdgeld-tijd-omrekenen',
      title: '🔄 Tijd Omrekenen',
      description: 'Uren, minuten en seconden omrekenen.',
      steps: [
        {
          title: 'Basisregels',
          content:
            '1 minuut = 60 seconden\n1 uur = 60 minuten\n1 dag = 24 uur\n1 week = 7 dagen',
        },
        {
          title: 'Voorbeeld',
          content:
            '3 min 15 sec = 195 seconden (3×60 + 15)\n1,5 uur = 90 minuten (1×60 + 30)',
        },
      ],
    },
    tijdzones: {
      id: 'tijdgeld-tijdzones',
      title: '🌍 Tijdzones',
      description: 'Tijdverschillen tussen landen.',
      steps: [
        {
          title: 'Hoe werkt het?',
          content:
            'Naar het westen = eerder (trek uren af)\nNaar het oosten = later (tel uren op)\n\nVoorbeeld: New York is 6 uur vroeger.\nHier 14:00 → New York 08:00.',
        },
      ],
    },

    // ── KALENDER ──
    kalender_basis: {
      id: 'tijdgeld-kalender-basis',
      title: '📅 Kalender Basis',
      description: 'Dagen, weken en maanden.',
      steps: [
        {
          title: 'Basisfeiten',
          content:
            '1 week = 7 dagen\n1 jaar = 12 maanden = 52 weken = 365 dagen\n1 kwartaal = 3 maanden',
        },
        {
          title: 'Dagen per maand',
          content:
            '30 dagen: april, juni, september, november\n31 dagen: januari, maart, mei, juli, augustus, oktober, december\nFebruari: 28 dagen (29 in een schrikkeljaar)',
        },
      ],
    },
    kalender_schrikkel: {
      id: 'tijdgeld-kalender-schrikkel',
      title: '📅 Schrikkeljaar & Kwartalen',
      description: 'Schrikkeljaren en kwartalen berekenen.',
      steps: [
        {
          title: 'Schrikkeljaar',
          content:
            'Een schrikkeljaar is deelbaar door 4.\n2024 → 2024 : 4 = 506 → schrikkeljaar!\nFebruari heeft dan 29 dagen.',
        },
        {
          title: 'Kwartaal',
          content:
            'Een kwartaal = 3 maanden.\n1e kwartaal: jan + feb + mrt\nTel de dagen van elke maand op.',
        },
      ],
    },

    // ── GELD TELLEN ──
    geld_munten: {
      id: 'tijdgeld-geld-munten',
      title: '💰 Geld Tellen & Samenstellen',
      description: 'Het juiste bedrag maken met munten en biljetten.',
      steps: [
        {
          content:
            '1 euro = 100 cent\n€2,50 = 250 cent\n\nBegin met de grootste munten/biljetten en vul aan met kleinere.',
        },
      ],
    },
    geld_wisselgeld: {
      id: 'tijdgeld-geld-wisselgeld',
      title: '🪙 Wisselgeld',
      description: 'Hoeveel krijg je terug?',
      steps: [
        {
          title: 'Strategie: Doortellen',
          content:
            'Tel door vanaf het bedrag dat je betaalt tot wat je geeft.\n\nVoorbeeld: €6,35 betalen met €10,00\n€6,35 → €6,40 (+5ct) → €6,50 (+10ct) → €7,00 (+50ct) → €10,00 (+€3)\nWisselgeld: €3,65',
        },
      ],
    },

    // ── GELD REKENEN ──
    boodschappen: {
      id: 'tijdgeld-boodschappen',
      title: '🛒 Boodschappen Rekenen',
      description: 'Totaalprijs berekenen.',
      steps: [
        {
          content:
            'Meerdere producten? Tel de prijzen op.\nMeerdere stuks? Vermenigvuldig prijs × aantal.\n\nVoorbeeld: 4 appels van €0,50 = 4 × €0,50 = €2,00',
        },
      ],
    },
    prijzen_vergelijken: {
      id: 'tijdgeld-prijzen-vergelijken',
      title: '🏷️ Prijzen Vergelijken',
      description: 'Wat is goedkoper per stuk of per liter?',
      steps: [
        {
          content:
            'Bereken de prijs per stuk (of per liter/kg):\nPrijs per stuk = totaalprijs ÷ aantal\n\nVoorbeeld: 6 eieren €2,40 → €2,40 ÷ 6 = €0,40 per ei',
        },
      ],
    },
    korting: {
      id: 'tijdgeld-korting',
      title: '🏷️ Korting Berekenen',
      description: 'Percentagekorting en aanbiedingen.',
      steps: [
        {
          title: 'Stappenplan',
          content:
            'Stap 1: Bereken het kortingsbedrag (percentage × prijs)\nStap 2: Trek het af van de originele prijs\n\nVoorbeeld: 25% korting op €40\n25% van 40 = 10. Nieuwe prijs: 40 - 10 = €30.',
        },
        {
          title: '3 halen, 2 betalen',
          content: 'Je bespaart 1 stuk.\nBesparing = prijs van 1 stuk.',
        },
      ],
    },
    sparen: {
      id: 'tijdgeld-sparen',
      title: '🐷 Sparen',
      description: 'Hoeveel heb je? Hoeveel moet je nog?',
      steps: [
        {
          content:
            'Hoeveel heb je nu? Tel je spaargeld + nieuw geld op.\nHoeveel moet je nog? Trek af: doel − wat je hebt = nog sparen.\nHoe lang duurt het? Nog sparen ÷ bedrag per week/maand.',
        },
      ],
    },

    // ── SNELHEID ──
    snelheid_berekenen: {
      id: 'tijdgeld-snelheid-berekenen',
      title: '🚗 Snelheid Berekenen',
      description: 'Afstand, snelheid en tijd — de driehoek.',
      steps: [
        {
          title: 'De drie formules',
          content:
            'Afstand = snelheid × tijd\nSnelheid = afstand : tijd\nTijd = afstand : snelheid\n\nTip: gebruik de AST-driehoek (Afstand boven, Snelheid en Tijd onder).',
        },
        {
          title: 'Voorbeeld',
          content:
            '90 km/u, 2,5 uur → 90 × 2,5 = 225 km\n25 km bij 100 km/u → 25 : 100 = 0,25 uur = 15 min',
        },
        {
          title: 'Meter per seconde',
          content:
            'km/u naar m/s: deel door 3,6\n60 km/u = 60.000 m : 3.600 s ≈ 16,7 m/s',
        },
      ],
    },

    // ── GRAFIEKEN ──
    tabellen_lezen: {
      id: 'tijdgeld-tabellen-lezen',
      title: '📊 Tabellen Lezen',
      description: 'Informatie uit tabellen halen.',
      steps: [
        {
          content:
            'Stap 1: Lees de kolomkoppen (wat staat er bovenaan?)\nStap 2: Zoek de juiste rij\nStap 3: Vergelijk de getallen\n\nTip: Het grootste getal = het meeste/langste/warmste',
        },
      ],
    },
    grafieken_lezen: {
      id: 'tijdgeld-grafieken-lezen',
      title: '📈 Grafieken Lezen',
      description: 'Informatie uit grafieken halen.',
      steps: [
        {
          title: 'Soorten grafieken',
          content:
            'Staafdiagram: vergelijk hoeveelheden\nLijndiagram: bekijk veranderingen over tijd\nCirkeldiagram: bekijk verdeling (altijd 100%)',
        },
        {
          title: 'Stijging berekenen',
          content:
            'Stijging = eindwaarde − beginwaarde\nStijging per uur = totale stijging : aantal uren',
        },
        {
          title: 'Cirkeldiagram',
          content:
            'Alle delen samen = 100%.\nOntbrekend deel = 100% − som van de andere delen.',
        },
      ],
    },
    patronen: {
      id: 'tijdgeld-patronen',
      title: '🔢 Patronen & Reeksen',
      description: 'Het patroon in een getallenrij ontdekken.',
      steps: [
        {
          content:
            'Stap 1: Kijk naar het verschil tussen opeenvolgende getallen\nStap 2: Is het verschil steeds hetzelfde? Dan is dat de "sprong"\nStap 3: Of verdubbelt het? (×2, ×2, ×2)\nStap 4: Tel de sprong op bij het laatste getal',
        },
      ],
    },

    // ── FINANCIEEL ──
    rente: {
      id: 'tijdgeld-rente',
      title: '🏦 Rente Berekenen',
      description: 'Enkelvoudige en samengestelde rente.',
      steps: [
        {
          title: 'Enkelvoudige rente',
          content:
            'Rente per jaar = percentage × bedrag\nVoorbeeld: 5% van €1000 = €50 per jaar\nNa 3 jaar: 3 × €50 = €150 rente',
        },
        {
          title: 'Rente over maanden',
          content:
            'Stap 1: Bereken de jaarrente\nStap 2: Deel door 12 voor de rente per maand\nStap 3: Vermenigvuldig met het aantal maanden',
        },
        {
          title: 'Samengestelde rente (rente op rente)',
          content:
            'Elk jaar bereken je rente over het nieuwe totaal.\nJaar 1: 10% van 1000 = 100 (totaal 1100)\nJaar 2: 10% van 1100 = 110 (totaal 1210)',
        },
      ],
    },
    loon: {
      id: 'tijdgeld-loon',
      title: '💶 Loon Berekenen',
      description: 'Uurloon en bruto/netto.',
      steps: [
        {
          title: 'Uurloon',
          content:
            'Uurloon = totaal verdiend : aantal uren\nVoorbeeld: €120 in 8 uur → 120 : 8 = €15 per uur',
        },
        {
          title: 'Bruto/Netto',
          content:
            'Bruto = wat je verdient vóór belasting\nNetto = wat je overhoudt ná belasting\nNetto = bruto − belasting',
        },
      ],
    },
    valuta: {
      id: 'tijdgeld-valuta',
      title: '💱 Valuta Omrekenen',
      description: 'Wisselkoersen gebruiken.',
      steps: [
        {
          title: 'Stappenplan',
          content:
            'Euro naar andere valuta: vermenigvuldig met de koers\nAndere valuta naar euro: deel door de koers\n\nVoorbeeld: 1 EUR = 1,10 USD\n50 EUR = 50 × 1,10 = 55 USD\n55 USD = 55 : 1,10 = 50 EUR',
        },
      ],
    },
    btw: {
      id: 'tijdgeld-btw',
      title: '🧾 BTW & Belasting',
      description: 'BTW berekenen en terugrekenen.',
      steps: [
        {
          title: 'BTW erbij',
          content:
            'Prijs inclusief BTW = prijs × (1 + BTW%)\nVoorbeeld: €100 + 21% BTW = €100 × 1,21 = €121',
        },
        {
          title: 'BTW eraf (terugrekenen)',
          content:
            'Prijs exclusief BTW = prijs inclusief : (1 + BTW%)\nVoorbeeld: €121 inclusief 21% → €121 : 1,21 = €100',
        },
      ],
    },

    // Legacy keys for backwards compatibility
    klokkijken: {
      id: 'tijdgeld-klokkijken',
      title: '🕐 Klokkijken',
      description: 'Analoog en digitaal klokkijken.',
      steps: [
        {
          content:
            'Kleine wijzer = uren\nGrote wijzer = minuten\nDunne wijzer = seconden\n\n1 uur = 60 minuten\n1 minuut = 60 seconden',
        },
        {
          title: 'Kwartieren',
          content:
            'Kwart over = 15 minuten na het hele uur\nHalf = 30 minuten na het hele uur\nKwart voor = 15 minuten voor het volgende uur',
        },
      ],
    },
    tijd_rekenen: {
      id: 'tijdgeld-tijd-rekenen',
      title: '⏳ Tijdsduur Berekenen',
      description: 'Hoelang duurt iets?',
      steps: [
        {
          title: 'Stappenplan',
          content:
            'Stap 1: Tel eerst naar het volgende hele uur\nStap 2: Tel dan de hele uren\nStap 3: Tel de resterende minuten',
        },
      ],
    },
    wisselgeld: {
      id: 'tijdgeld-wisselgeld',
      title: '🪙 Wisselgeld',
      description: 'Hoeveel krijg je terug?',
      steps: [
        {
          title: 'Strategie: Doortellen',
          content: 'Tel door vanaf het bedrag dat je betaalt tot wat je geeft.',
        },
      ],
    },
    geld_samenstellen: {
      id: 'tijdgeld-geld-samenstellen',
      title: '💰 Geld Samenstellen',
      description: 'Het juiste bedrag maken.',
      steps: [
        {
          content:
            '1 euro = 100 cent\nBegin met de grootste munten/biljetten en vul aan met kleinere.',
        },
      ],
    },
    tabellen: {
      id: 'tijdgeld-tabellen',
      title: '📊 Tabellen Lezen',
      description: 'Informatie uit tabellen halen.',
      steps: [
        {
          content:
            'Lees de kolomkoppen. Zoek de juiste rij. Vergelijk de getallen.',
        },
      ],
    },
    snelheid: {
      id: 'tijdgeld-snelheid',
      title: '🚗 Snelheid',
      description: 'Afstand, snelheid en tijd.',
      steps: [
        {
          content:
            'Afstand = snelheid × tijd\nSnelheid = afstand : tijd\nTijd = afstand : snelheid',
        },
      ],
    },
    grafieken: {
      id: 'tijdgeld-grafieken',
      title: '📈 Grafieken',
      description: 'Informatie uit grafieken halen.',
      steps: [
        {
          content:
            'Staafdiagram: vergelijk hoeveelheden\nLijndiagram: veranderingen over tijd\nCirkeldiagram: verdeling (altijd 100%)',
        },
      ],
    },
  },

  // ═══════════════════════════════════════════
  // WERKWOORDSPELLING
  // ═══════════════════════════════════════════
  SpellingWW: {
    stam: {
      id: 'spellingww-stam',
      title: '🔍 De Stam Vinden',
      description: 'Het hele werkwoord min -en.',
      steps: [
        {
          title: 'Stappenplan',
          content:
            'De stam = het hele werkwoord min -en.\nlopen → loop\nmaken → maak\nvinden → vind\nspelen → speel',
        },
        {
          title: 'Let op!',
          content:
            'Soms moet je de klinker verdubbelen:\nlopen - en = lop → loop (open lettergreep)\nspelen - en = spel → speel',
        },
      ],
    },
    tt_ik: {
      id: 'spellingww-tt-ik',
      title: '👤 Ik-vorm (Tegenwoordige Tijd)',
      description: 'Bij "ik" gebruik je alleen de stam.',
      steps: [
        {
          title: 'Regel',
          content:
            'Bij "ik" gebruik je ALLEEN de stam.\nik loop, ik maak, ik vind, ik werk',
        },
        {
          title: 'Let op bij stam op -d',
          content:
            'Stam eindigt op d? Gewoon de stam schrijven.\nik vind (niet: ik vindt)\nik vermoed (niet: ik vermoedt)',
        },
      ],
    },
    tt_hij: {
      id: 'spellingww-tt-hij',
      title: '👤 Hij/Zij-vorm (Tegenwoordige Tijd)',
      description: 'Bij hij/zij/het: stam + t.',
      steps: [
        {
          title: 'Regel',
          content:
            'Bij hij, zij, het schrijf je: stam + t.\nhij loopt, zij maakt, het regent',
        },
        {
          title: 'Ook bij namen en dingen',
          content:
            'De hond = hij → blaft\nMijn broer = hij → speelt\nDe juf = zij → vertelt',
        },
      ],
    },
    tt_jij: {
      id: 'spellingww-tt-jij',
      title: '👤 Jij Ervoor (Tegenwoordige Tijd)',
      description: 'Jij staat ervoor? Dan stam + t.',
      steps: [
        {
          title: 'Regel',
          content:
            'Jij staat VOOR het werkwoord → stam + t\nJij loopt, jij werkt, jij maakt',
        },
      ],
    },
    tt_jij_inversie: {
      id: 'spellingww-tt-jij-inversie',
      title: '🔄 Jij-Omkering (Inversie)',
      description: 'Jij staat erachter? Geen -t!',
      steps: [
        {
          content:
            'Jij staat ACHTER het werkwoord → alleen stam (geen t!)\n"Loop jij naar school?"\n"Werk je vandaag?"\n"Vind jij dat ook?"',
        },
        {
          title: 'Vergelijk',
          content:
            'Jij loopt (jij ervoor → +t)\nLoop jij? (jij erachter → geen t)',
        },
      ],
    },
    tt_wij: {
      id: 'spellingww-tt-wij',
      title: '👥 Meervoud (Tegenwoordige Tijd)',
      description: 'Bij wij/jullie/zij: hele werkwoord.',
      steps: [
        {
          title: 'Regel',
          content:
            'Bij wij, jullie, zij (meervoud) gebruik je het hele werkwoord.\nwij lopen, jullie spelen, zij werken',
        },
      ],
    },
    tt_dt: {
      id: 'spellingww-tt-dt',
      title: '📝 Stam op -d (dt-spelling)',
      description: 'Wanneer schrijf je dt?',
      steps: [
        {
          title: 'Regel',
          content:
            'Eindigt de stam op een d? Dan schrijf je bij hij/zij: d + t = dt.\nhij vindt (stam = vind)\nhij wordt (stam = word)\nhij verzendt (stam = verzend)',
        },
        {
          title: 'Ik-vorm: geen t!',
          content:
            'Bij "ik" alleen de stam:\nik vind (niet: ik vindt)\nik word (niet: ik wordt)',
        },
      ],
    },
    vt_kofschip: {
      id: 'spellingww-vt-kofschip',
      title: "🚢 Verleden Tijd: 't Kofschip (+te/+ten)",
      description: 'Stam eindigt op kofschip-letter? Dan +te(n).',
      steps: [
        {
          content:
            "De letters van 't Kofschip: T, K, F, S, CH, P\n\nEindigt de stam op één van deze letters?\n→ Verleden tijd: stam + te (enkelvoud) of + ten (meervoud)\n\nVoorbeelden:\nwerk → werkte/werkten (K)\nfiets → fietste/fietsten (S)\nblaf → blafte (F)",
        },
      ],
    },
    vt_geen_kofschip: {
      id: 'spellingww-vt-geen-kofschip',
      title: '🚢 Verleden Tijd: Geen Kofschip (+de/+den)',
      description: 'Stam eindigt NIET op kofschip? Dan +de(n).',
      steps: [
        {
          content:
            "Eindigt de stam NIET op 't kofschip?\n→ Verleden tijd: stam + de (enkelvoud) of + den (meervoud)\n\nVoorbeelden:\nleer → leerde/leerden (R)\ngebeur → gebeurde (R)\ngooi → gooide (I)",
        },
      ],
    },
    vt_sterk: {
      id: 'spellingww-vt-sterk',
      title: '💪 Sterke Werkwoorden (Verleden Tijd)',
      description: 'Klinkerverandering in de verleden tijd.',
      steps: [
        {
          title: 'Wat zijn sterke werkwoorden?',
          content:
            'Bij sterke werkwoorden verandert de klinker:\nlopen → liep\nzingen → zong\nhelpen → hielp\nschrijven → schreef',
        },
        {
          title: 'Geen -te of -de!',
          content:
            'Sterke werkwoorden krijgen GEEN -te of -de.\nNIET: loopte, zingde\nWEL: liep, zong',
        },
      ],
    },
    vt_lastig: {
      id: 'spellingww-vt-lastig',
      title: '⚠️ Lastige Verleden Tijd',
      description: 'V/f-wissel, z/s-wissel, dubbele letters, Engels.',
      steps: [
        {
          title: 'V/F-wissel',
          content:
            'Stam is verf, maar het werkwoord is verVen.\nKijk naar de V (niet in kofschip) → verfde\n\nRegel: kijk altijd naar het hele werkwoord!',
        },
        {
          title: 'Z/S-wissel',
          content:
            'Stam is verhuis, maar het werkwoord is verhuiZen.\nZ niet in kofschip → verhuisde',
        },
        {
          title: 'Dubbele letters',
          content:
            'Stam op d + de = dubbel d: red → redde\nStam op t + te = dubbel t: wacht → wachtte',
        },
        {
          title: 'Engelse werkwoorden',
          content:
            'Kijk naar de KLANK van de stam:\nrace (klank s) → racete\ndownload (klank d) → downloadde',
        },
      ],
    },
    vd_kofschip: {
      id: 'spellingww-vd-kofschip',
      title: '✅ Voltooid Deelwoord: Ge...t',
      description: 'Kofschip-letter? Dan ge + stam + t.',
      steps: [
        {
          content:
            "Stam eindigt op 't kofschip letter?\n→ ge + stam + t\n\nVoorbeelden:\nwerk → gewerkt (K)\ndans → gedanst (S)\npost → gepost (T, al een t dus geen extra)",
        },
      ],
    },
    vd_geen_kofschip: {
      id: 'spellingww-vd-geen-kofschip',
      title: '✅ Voltooid Deelwoord: Ge...d',
      description: 'Geen kofschip? Dan ge + stam + d.',
      steps: [
        {
          content:
            "Stam eindigt NIET op 't kofschip?\n→ ge + stam + d\n\nVoorbeelden:\nred → gered (D)\nbouw → gebouwd (W)\nstofzuig → gestofzuigd (G)",
        },
      ],
    },
    vd_sterk: {
      id: 'spellingww-vd-sterk',
      title: '💪 Voltooid Deelwoord: Sterk',
      description: 'Onregelmatige werkwoorden met ge...en.',
      steps: [
        {
          title: 'Sterke werkwoorden',
          content:
            'Sterke werkwoorden hebben een onregelmatig voltooid deelwoord:\nschrijven → geschreven\neten → gegeten\nzingen → gezongen\nwinnen → gewonnen',
        },
        {
          title: 'Tip',
          content:
            'Deze moet je uit je hoofd leren! Gebruik de "heb-test":\nIk heb ge... → klinkt het goed?',
        },
      ],
    },
    vd_voorvoegsel: {
      id: 'spellingww-vd-voorvoegsel',
      title: '🔗 Voltooid Deelwoord met Voorvoegsel',
      description: 'Bij be-, ge-, ver-, her-, ont-: geen extra ge-.',
      steps: [
        {
          title: 'Regel',
          content:
            'Bij werkwoorden met be-, ge-, ver-, her-, ont- komt er GEEN extra ge- voor.\nbeloven → beloofd (niet gebeloofd)\nvertellen → verteld (niet geverteld)\nontdekken → ontdekt (niet geontdekt)\nverhuizen → verhuisd (niet geverhuisd)',
        },
        {
          title: 'Scheidbare werkwoorden',
          content:
            'Bij scheidbare werkwoorden komt ge- ertussen:\nuitzenden → uitgezonden\nopbellen → opgebeld',
        },
      ],
    },
    bn_vd: {
      id: 'spellingww-bn-vd',
      title: '📎 Bijvoeglijk Gebruikt Voltooid Deelwoord',
      description: 'De geverfde muur, de gebroken vaas.',
      steps: [
        {
          title: 'Regel',
          content:
            'Als het voltooid deelwoord als bijvoeglijk naamwoord wordt gebruikt,\nmaak je het zo kort mogelijk + e:\n\nverbrand → verbrande (auto)\ngered → geredde (jongen, korte klinker → dubbel d)\ngeverfd → geverfde (muur)\ngelapt → gelapte (ramen)',
        },
        {
          title: 'Let op korte klinkers',
          content:
            'Bij een korte klinker verdubbel je de medeklinker:\ngered → geredde (niet: gerede)\ngepot → gepotte (niet: gepote)\n\nBij een lange klinker niet:\ngepoot → gepote (niet: gepotte)\nbesteed → bestede (niet: besteedde)',
        },
      ],
    },
    homofonen: {
      id: 'spellingww-homofonen',
      title: '👂 Homofonen (Gelijk Klinkend)',
      description: 'Lijdt/leidt, word/wordt.',
      steps: [
        {
          title: 'Leiden vs. Lijden',
          content:
            'Leiden (ei) = de weg wijzen, aanvoeren\nLijden (ij) = pijn hebben, ondergaan\n\nHij leidt de vergadering.\nHij lijdt aan hoofdpijn.',
        },
        {
          title: 'Word vs. Wordt',
          content:
            'Ik word (stam, geen t)\nHij wordt (stam + t)\nWord jij? (inversie, geen t)',
        },
      ],
    },
    gebiedende_wijs: {
      id: 'spellingww-gebiedende-wijs',
      title: '📢 Gebiedende Wijs',
      description: 'Een bevel of opdracht geven.',
      steps: [
        {
          title: 'Regel',
          content:
            'De gebiedende wijs is alleen de stam (geen t!).\nLoop!\nWerk harder!\nWord lid! (niet: wordt lid)',
        },
      ],
    },
    // Legacy keys for backwards compatibility with old hint lookups
    werkwoord_tegenwoordige_tijd: {
      id: 'spellingww-tt',
      title: '📝 Tegenwoordige Tijd',
      description: 'Werkwoorden in de tegenwoordige tijd.',
      steps: [
        {
          title: 'De Stam Vinden',
          content:
            'De stam = het hele werkwoord min -en.\nlopen → loop\nmaken → maak\nvinden → vind',
        },
        {
          title: 'Wie doet het?',
          content:
            'ik → stam (ik loop)\njij ervoor / hij / zij → stam + t (jij loopt, hij loopt)\njij erachter → stam (loop jij?)\nwij / jullie / zij → hele werkwoord (wij lopen)',
        },
      ],
    },
    werkwoord_verleden_tijd: {
      id: 'spellingww-vt',
      title: "🕰️ Verleden Tijd (t'Kofschip)",
      description: 'Werkwoorden in de verleden tijd.',
      steps: [
        {
          title: 'Stap 1: Vind de stam',
          content:
            'Het hele werkwoord min -en.\nwerken → werk\nfietsen → fiets',
        },
        {
          title: "Stap 2: Check 't Kofschip",
          content:
            'Eindigt de stam op T, K, F, S, CH of P?\nJA → +te (ik werkte) of +ten (wij werkten)\nNEE → +de (ik leefde) of +den (wij leefden)',
        },
      ],
    },
    werkwoord_voltooid_deelwoord: {
      id: 'spellingww-vd',
      title: '🏁 Voltooid Deelwoord',
      description: 'Ge...t of ge...d?',
      steps: [
        {
          title: 'Stappenplan',
          content:
            "Stap 1: Vind de stam (werkwoord - en)\nStap 2: Check 't kofschip\nStap 3: Maak het voltooid deelwoord:\n  Kofschip? → ge + stam + t (gewerkt)\n  Geen kofschip? → ge + stam + d (geleefd)",
        },
        {
          title: 'Voorvoegsels',
          content:
            'Bij be-, ge-, ver-, her-, ont- komt er GEEN extra ge- voor.\nbelooft → beloofd (niet gebeloofd)\nvertelt → verteld (niet geverteld)',
        },
      ],
    },
    kofschip: {
      id: 'spellingww-kofschip',
      title: "🚢 't Kofschip",
      description: 'Wanneer +te en wanneer +de?',
      steps: [
        {
          content:
            "De letters van 't Kofschip: T, K, F, S, CH, P\n\nEindigt de stam op één van deze letters?\n→ JA: +te(n) of ge...t\n→ NEE: +de(n) of ge...d",
        },
      ],
    },
    jij_omkering: {
      id: 'spellingww-jij-omkering',
      title: '🔄 Jij-Omkering',
      description: 'Wanneer wel en geen -t bij jij.',
      steps: [
        {
          content:
            'Jij staat VOOR het werkwoord → stam + t\n"Jij loopt naar school."\n\nJij staat ACHTER het werkwoord → alleen stam (geen t!)\n"Loop jij naar school?"',
        },
      ],
    },
  },

  // ═══════════════════════════════════════════
  // SPELLINGREGELS & GRAMMATICA
  // ═══════════════════════════════════════════
  SpellingRegel: {
    // ── KLANKEN_SPELLING ──
    lange_klank: {
      id: 'spellingregel-lange-klank',
      title: '🔊 Lange Klanken',
      description: 'Wanneer schrijf je één of twee klinkers?',
      steps: [
        {
          title: 'Open lettergreep',
          content:
            'Lange klank aan het eind van een klankgroep? Schrijf maar één klinker.\nbo-men (niet boo-men)\nlo-pen (niet loo-pen)\nre-gen (niet ree-gen)',
        },
        {
          title: 'Gesloten lettergreep',
          content:
            'Lange klank in een gesloten lettergreep? Schrijf twee klinkers.\nboom, maan, been',
        },
      ],
    },
    korte_klank: {
      id: 'spellingregel-korte-klank',
      title: '🔉 Korte Klanken',
      description: 'Wanneer schrijf je een dubbele medeklinker?',
      steps: [
        {
          title: 'Regel',
          content:
            'Korte klank? Dan dubbele medeklinker in het meervoud.\npot → pot-ten\nbak → bak-ken\nren → ren-nen',
        },
      ],
    },
    v_f_wissel: {
      id: 'spellingregel-vf-wissel',
      title: '🔄 V/F-wisseling',
      description: 'Wanneer schrijf je v en wanneer f?',
      steps: [
        {
          title: 'Regel',
          content:
            'Woord eindigt op f? Als je het langer maakt, wordt het een v.\nlief → lieve\nbraaf → brave\nneef → neven',
        },
      ],
    },
    s_z_wissel: {
      id: 'spellingregel-sz-wissel',
      title: '🔄 S/Z-wisseling',
      description: 'Wanneer schrijf je s en wanneer z?',
      steps: [
        {
          title: 'Regel',
          content:
            'Woord eindigt op s? Als je het langer maakt, wordt het een z.\nhuis → huizen\nglas → glazen',
        },
      ],
    },
    eind_d_t: {
      id: 'spellingregel-eind-dt',
      title: '🔚 Eind-d of eind-t?',
      description: 'Hoe weet je of een woord op d of t eindigt?',
      steps: [
        {
          title: 'Regel: Langer maken',
          content:
            'Maak het woord langer. Hoor je een d? Schrijf een d.\nhond → honden (d)\nbed → bedden (d)\nhard → harde (d)',
        },
        {
          title: 'Voltooid deelwoord',
          content:
            "Stam eindigt NIET op 't kofschip? Dan eindigt het voltooid deelwoord op -d.\nbezeer → bezeerd\nleer → geleerd",
        },
      ],
    },
    algemeen: {
      id: 'spellingregel-algemeen',
      title: '📖 Algemene Spellingregels',
      description: 'Veelvoorkomende spellingregels.',
      steps: [
        {
          title: '-lijk regel',
          content:
            'Woorden op -lijk klinken als "luk", maar je schrijft altijd -lijk.\ngevaarlijk, heerlijk, eerlijk',
        },
      ],
    },

    // ── WEETWOORDEN ──
    weetwoorden_eau: {
      id: 'spellingregel-weetwoorden-eau',
      title: '🎁 Weetwoorden met -eau',
      description: 'Woorden met eau uit het Frans.',
      steps: [
        {
          content:
            'Sommige woorden schrijf je met -eau.\nVoorbeelden: cadeau, bureau, niveau, plateau',
        },
        {
          title: 'Tip',
          content:
            'Deze woorden komen uit het Frans. Je moet ze uit je hoofd leren!',
        },
      ],
    },
    weetwoorden_accent: {
      id: 'spellingregel-weetwoorden-accent',
      title: '☕ Weetwoorden met accent',
      description: 'Woorden met é, woorden met -ee, woorden met -ie.',
      steps: [
        {
          title: 'Woorden met é',
          content:
            'Sommige woorden schrijf je met é (accent aigu).\nVoorbeelden: café, logé',
        },
        {
          title: 'Woorden met -ee',
          content:
            'Sommige woorden eindigen op -ee (zonder accent).\nVoorbeelden: idee, zee, thee, fee',
        },
        {
          title: 'Woorden met -ie',
          content:
            'Sommige woorden eindigen op -ie.\nVoorbeelden: koffie, politie, energie',
        },
      ],
    },
    weetwoorden_c: {
      id: 'spellingregel-weetwoorden-c',
      title: '🔤 De letter C',
      description: 'Wanneer klinkt de c als s en wanneer als k?',
      steps: [
        {
          title: 'C als S-klank',
          content:
            'C + e/i = S-klank\nVoorbeelden: cent, circus, citroen, cijfer',
        },
        {
          title: 'C als K-klank',
          content:
            'C + a/o/u = K-klank\nVoorbeelden: cola, cactus, computer, cake',
        },
      ],
    },
    weetwoorden_tie_age: {
      id: 'spellingregel-weetwoorden-tie-age',
      title: '📝 Woorden met -tie en -age',
      description: 'Leenwoorden met bijzondere uitgangen.',
      steps: [
        {
          title: '-tie woorden',
          content:
            'Je hoort "tsie", maar schrijft "tie".\nVoorbeelden: politie, informatie, operatie, vacatie, natie',
        },
        {
          title: '-age woorden',
          content:
            'Je hoort "aazje", maar schrijft "age".\nVoorbeelden: bagage, garage, etage, massage',
        },
        {
          title: '-tion woorden',
          content:
            'Je hoort "tsion", maar schrijft "tion".\nVoorbeelden: station, actie',
        },
      ],
    },
    weetwoorden_frans: {
      id: 'spellingregel-weetwoorden-frans',
      title: '🇫🇷 Franse & Engelse Leenwoorden',
      description: 'Woorden uit andere talen die je moet onthouden.',
      steps: [
        {
          title: 'Leenwoorden uit het Frans',
          content:
            'chef (niet sjef)\nroute (niet roete)\ntrottoir (niet trotwaar)\nmiljonair (niet miljonar)\ncaissière',
        },
        {
          title: 'Engelse leenwoorden',
          content: 'computer (met c, niet k)\nteam (niet tiem)',
        },
        {
          title: 'Bijzondere letters',
          content: 'taxi (x = ks)\nthee (th)\nliniaal (ia)',
        },
      ],
    },
    weetwoorden_moeilijk: {
      id: 'spellingregel-weetwoorden-moeilijk',
      title: '🧠 Moeilijke Weetwoorden',
      description: 'Lastige woorden met dubbele letters.',
      steps: [
        {
          title: 'Dubbele medeklinkers',
          content:
            'portemonnee (dubbel n, dubbel e)\naccommodatie (dubbel c, dubbel m)\ninteressant (dubbel s)\nmillennium (dubbel l, dubbel n)\nabonnement (dubbel n)',
        },
        {
          title: 'Overige moeilijke woorden',
          content:
            'onmiddellijk (dubbel d, dubbel l)\nagressie (één g, dubbel s)\ncommissaris (dubbel m, dubbel s)\nfaillissement (dubbel l, dubbel s)\nparallel (één r, dubbel l)',
        },
        {
          title: 'Tip',
          content:
            'Schrijf deze woorden vaak over. Zo onthoud je de moeilijke stukken!',
        },
      ],
    },

    // ── SCHRIJFWIJZE ──
    trema: {
      id: 'spellingregel-trema',
      title: '¨ Trema',
      description: 'Wanneer gebruik je een trema (twee puntjes)?',
      steps: [
        {
          title: 'Meervoud',
          content:
            'Een trema voorkomt dat je twee klinkers als één klank leest.\nideeën (niet ideeen)\nzeeën (niet zeeen)\ntweeën',
        },
        {
          title: 'Na ge-, be-, ver-',
          content: 'geëist, geüpload, geëindigd',
        },
        {
          title: 'Werkwoorden en bijvoeglijk naamwoorden',
          content: 'kopiëren, financiële',
        },
        {
          title: 'Klemtoon op de ie?',
          content:
            'Klemtoon op de ie? Dan ieën: knieën\nKlemtoon NIET op de ie? Dan iën: bacteriën',
        },
      ],
    },
    meervoud: {
      id: 'spellingregel-meervoud',
      title: '📝 Meervoud',
      description: 'Enkelvoud naar meervoud.',
      steps: [
        {
          title: 'Korte klinker',
          content:
            'Korte klinker → dubbele medeklinker in meervoud.\npan → pannen\nkat → katten',
        },
        {
          title: 'Lange klinker',
          content:
            'Lange klinker → enkele medeklinker in meervoud.\nmaan → manen\nboom → bomen',
        },
        {
          title: "Meervoud met 's",
          content:
            "Woorden op -y, -a, -o, -u krijgen 's.\nbaby → baby's\nauto → auto's",
        },
      ],
    },
    s_genitief: {
      id: 'spellingregel-s-genitief',
      title: "⏰ 's morgens, 's avonds",
      description: 'Tijdsaanduidingen met apostrof-s.',
      steps: [
        {
          title: 'Regel',
          content:
            "Het is een afkorting van 'des morgens', 'des avonds', etc.\nDe apostrof staat op de plek van 'de'.\n\n's morgens, 's middags, 's avonds, 's nachts",
        },
      ],
    },
    hoofdletter: {
      id: 'spellingregel-hoofdletter',
      title: '🔠 Hoofdletters',
      description: 'Wanneer schrijf je een hoofdletter?',
      steps: [
        {
          title: 'Regels',
          content:
            'Hoofdletters voor:\n- Begin van een zin\n- Namen (personen, steden, landen)\n- Het woord "ik"\n\nGeen hoofdletter voor: dagen, maanden, seizoenen',
        },
      ],
    },
    tussen_n_s: {
      id: 'spellingregel-tussen-ns',
      title: '🔗 Tussen-n en Tussen-s',
      description: 'Wanneer komt er een n of s tussen twee woorden?',
      steps: [
        {
          title: 'Tussen-n',
          content:
            'Als het eerste deel een meervoud op -en heeft:\npannen + koek = pannenkoek\npaarden + staart = paardenstaart',
        },
        {
          title: 'Uitzondering',
          content:
            'Als er maar één is, geen tussen-n:\nzonnebril (er is maar één zon)\nkoninginnedag (maar: koninginnendag mag ook)',
        },
      ],
    },
    samenstelling: {
      id: 'spellingregel-samenstelling',
      title: '🧩 Samenstellingen',
      description: 'Woorden aan elkaar schrijven.',
      steps: [
        {
          title: 'Regel',
          content:
            'Samenstellingen schrijf je aan elkaar.\nmiddel + eeuwen = middeleeuwen\npyjama + broek = pyjamabroek',
        },
        {
          title: 'Geen streepje',
          content:
            'In het Nederlands schrijf je samenstellingen meestal aan elkaar, niet met een streepje.',
        },
      ],
    },

    // ── ZINSONTLEDING ──
    onderwerp: {
      id: 'spellingregel-onderwerp',
      title: '👤 Onderwerp',
      description: 'Wie of wat doet iets?',
      steps: [
        {
          title: 'Hoe vind je het?',
          content:
            'Vraag: "Wie of wat + persoonsvorm?"\n\nVoorbeeld: De kinderen spelen buiten.\nWie spelen? → De kinderen = onderwerp',
        },
        {
          title: 'Let op!',
          content:
            'Het onderwerp staat niet altijd vooraan!\n"Op het plein staat een grote boom."\nWat staat? → een grote boom = onderwerp',
        },
      ],
    },
    persoonsvorm: {
      id: 'spellingregel-persoonsvorm',
      title: '🔧 Persoonsvorm',
      description: 'Het werkwoord dat verandert met de tijd.',
      steps: [
        {
          title: 'Hoe vind je het?',
          content:
            'Verander de tijd van de zin. Het woord dat verandert is de persoonsvorm.\n\n"Sophie rent naar school" → "Sophie rende naar school"\nrent → rende, dus "rent" is de persoonsvorm.',
        },
      ],
    },
    lijdend_voorwerp: {
      id: 'spellingregel-lijdend-voorwerp',
      title: '🎯 Lijdend Voorwerp',
      description: 'Wat of wie ondergaat de handeling?',
      steps: [
        {
          title: 'Hoe vind je het?',
          content:
            'Vraag: "Wie/wat + persoonsvorm + onderwerp?"\n\nVoorbeeld: De kok bakt een pannenkoek.\nWat bakt de kok? → een pannenkoek = lijdend voorwerp',
        },
      ],
    },
    meewerkend_voorwerp: {
      id: 'spellingregel-meewerkend-voorwerp',
      title: '🎁 Meewerkend Voorwerp',
      description: 'Aan wie of voor wie?',
      steps: [
        {
          title: 'Hoe vind je het?',
          content:
            'Vraag: "Aan wie ...?" of "Voor wie ...?"\n\nVoorbeeld: Ik geef mijn zus een cadeau.\nAan wie geef ik? → mijn zus = meewerkend voorwerp',
        },
      ],
    },

    // ── WOORDSOORTEN ──
    woordsoorten: {
      id: 'spellingregel-woordsoorten',
      title: '📦 Woordsoorten',
      description: 'Welk woordsoort is het?',
      steps: [
        {
          title: 'Overzicht',
          content:
            'Zelfstandig naamwoord: mens, dier, ding (de/het ervoor)\nBijvoeglijk naamwoord: hoe is iets? (groot, rood)\nWerkwoord: wat doe je? (lopen, zijn)\nBijwoord: zegt iets over het werkwoord (snel, vaak)\nLidwoord: de, het, een',
        },
      ],
    },
    voorzetsels: {
      id: 'spellingregel-voorzetsels',
      title: '📍 Voorzetsels',
      description: 'Woorden die een relatie aangeven.',
      steps: [
        {
          title: 'Wat zijn voorzetsels?',
          content:
            'Voorzetsels geven een relatie aan (plaats, tijd, richting).\nVoorbeelden: in, op, aan, met, voor, naar, bij, door, over, uit, van, tot',
        },
      ],
    },
    voegwoorden: {
      id: 'spellingregel-voegwoorden',
      title: '🔗 Voegwoorden',
      description: 'Woorden die zinnen verbinden.',
      steps: [
        {
          title: 'Nevenschikkend',
          content:
            'Verbinden gelijkwaardige zinsdelen:\nen, of, maar, want, dus',
        },
        {
          title: 'Onderschikkend',
          content:
            'Maken een bijzin:\ndat, omdat, als, wanneer, hoewel, terwijl',
        },
      ],
    },

    // ── LEESTEKENS ──
    leestekens: {
      id: 'spellingregel-leestekens',
      title: '❗ Leestekens',
      description: 'Punt, vraagteken, uitroepteken en komma.',
      steps: [
        {
          title: 'Welk leesteken?',
          content:
            '. (punt) → gewone mededeling\n? (vraagteken) → vraag\n! (uitroepteken) → bevel of uitroep\n, (komma) → pauze in de zin',
        },
        {
          title: 'Komma na bijzin',
          content:
            'Begint de zin met een bijzin? Dan komt er een komma.\n"Als het regent, neem ik een paraplu mee."',
        },
      ],
    },
  },

  // ═══════════════════════════════════════════
  // TAALBEGRIP
  // ═══════════════════════════════════════════
  Taalbegrip: {
    // ── TEKSTBEGRIP (Begrijpen wat er staat) ──
    letterlijk_begrip: {
      id: 'taalbegrip-letterlijk-begrip',
      title: '📖 Letterlijk Begrip',
      description: 'Het antwoord staat in de tekst.',
      steps: [
        {
          title: 'Strategie',
          content:
            'Lees de vraag goed. Zoek het antwoord letterlijk in de tekst.\nLet op signaalwoorden als "daarom", "want", "omdat" — die geven de reden aan.',
        },
        {
          title: 'Tip',
          content:
            'Pas op voor afleidende getallen! Als de tekst zegt "5 boeken" en "3 weken", lees dan goed welk getal bij de vraag hoort.',
        },
      ],
    },
    informatief_lezen: {
      id: 'taalbegrip-informatief-lezen',
      title: '📋 Informatief Lezen',
      description: "Informatie halen uit schema's, borden en recepten.",
      steps: [
        {
          title: 'Strategie',
          content:
            'Lees eerst de koppen en labels. Zoek dan het specifieke gegeven dat de vraag zoekt.\nBij tabellen: volg de rij en kolom tot ze kruisen.\nBij borden/uitnodigingen: zoek het juiste kopje (Waar?, Wanneer?, Prijs?).',
        },
        {
          title: 'Tip',
          content:
            'Let goed op eenheden en voorwaarden. "Kinderen t/m 12 jaar" betekent 12 jaar en jonger.',
        },
      ],
    },
    volgorde: {
      id: 'taalbegrip-volgorde',
      title: '🔢 Volgorde Bepalen',
      description: 'Gebeurtenissen in de juiste volgorde zetten.',
      steps: [
        {
          title: 'Strategie',
          content:
            'Let op signaalwoorden voor volgorde:\n"eerst", "daarna", "vervolgens", "ten slotte", "uiteindelijk"\n\nLees de tekst stap voor stap en nummer de gebeurtenissen.',
        },
        {
          title: 'Tip',
          content:
            'Soms staat de volgorde niet letterlijk in de tekst. Denk dan logisch na: wat moet er EERST gebeuren voordat het volgende kan?',
        },
      ],
    },

    // ── WOORDENSCHAT (Woorden en uitdrukkingen kennen) ──
    woordbetekenis: {
      id: 'taalbegrip-woordbetekenis',
      title: '📚 Woordbetekenis',
      description: 'Moeilijke woorden begrijpen uit de context.',
      steps: [
        {
          title: 'Strategie',
          content:
            'Ken je het woord niet? Kijk naar:\n1. De context (de zinnen eromheen)\n2. Woorddelen (on- = niet, -baar = in staat om, -loos = zonder)\n3. Vergelijkbare woorden die je wél kent',
        },
        {
          title: 'Handige woorddelen',
          content:
            'on- = niet (onzinkbaar = niet zinkbaar)\n-baar = geschikt om te (eetbaar, leesbaar)\n-loos = zonder (dakloos, werkloos)\npre- = voor (preventief = voorkomend)\nre- = opnieuw (recyclen = opnieuw gebruiken)',
        },
        {
          title: 'Voorbeelden',
          content:
            'uitstekend = heel goed\ngemiddeld = ongeveer, als je het uitrekent\nfiguurlijk = niet letterlijk bedoeld\nlegitiem = wettig/geldig\nsimultaan = gelijktijdig\nconsequent = regelmatig, volgens vast patroon',
        },
      ],
    },
    spreekwoorden: {
      id: 'taalbegrip-spreekwoorden',
      title: '🗣️ Spreekwoorden & Gezegden',
      description: 'Vaste uitdrukkingen met een figuurlijke betekenis.',
      steps: [
        {
          title: 'Wat is een spreekwoord?',
          content:
            'Een spreekwoord is een vaste uitdrukking die je NIET letterlijk moet nemen.\nDe betekenis is figuurlijk (overdrachtelijk).',
        },
        {
          title: 'Voorbeelden',
          content:
            '"De kat uit de boom kijken" = even afwachten\n"Met de deur in huis vallen" = meteen ter zake komen\n"Een appeltje voor de dorst" = geld opzij leggen voor later\n"Het regent pijpenstelen" = het regent heel hard',
        },
        {
          title: 'Tip',
          content:
            'Als een antwoord te letterlijk klinkt (bijv. "bang zijn voor katten"), is het waarschijnlijk fout. Zoek de figuurlijke betekenis.',
        },
      ],
    },
    taalbegrippen: {
      id: 'taalbegrip-taalbegrippen',
      title: '📘 Taalbegrippen',
      description: 'Begrippen over taal kennen.',
      steps: [
        {
          title: 'Belangrijke begrippen',
          content:
            'Synoniem = woord met dezelfde betekenis (blij/vrolijk)\nAntoniem = woord met tegenovergestelde betekenis (groot/klein)\nHomoniem = zelfde woord, andere betekenis (bank = zitmeubel / geldbedrijf)\nNeologisme = nieuw woord\nPleonasme = dubbelop (witte sneeuw)\nPlagiaat = werk van een ander stelen',
        },
      ],
    },

    // ── TEKSTANALYSE (Tekst analyseren en interpreteren) ──
    hoofdgedachte: {
      id: 'taalbegrip-hoofdgedachte',
      title: '💡 Hoofdgedachte',
      description: 'Waar gaat de tekst over?',
      steps: [
        {
          title: 'Strategie',
          content:
            'De hoofdgedachte is het belangrijkste punt van de tekst.\nVraag jezelf: "Als ik de tekst in één zin moet samenvatten, wat zou ik zeggen?"',
        },
        {
          title: 'Titel kiezen',
          content:
            'Een goede titel dekt de hele tekst, niet slechts één detail.\nDe titel moet passen bij het onderwerp én de boodschap.',
        },
      ],
    },
    conclusie_trekken: {
      id: 'taalbegrip-conclusie-trekken',
      title: '🧠 Conclusie Trekken',
      description: 'Wat kun je afleiden uit de tekst?',
      steps: [
        {
          title: 'Strategie',
          content:
            'Een conclusie staat NIET letterlijk in de tekst.\nJe combineert informatie uit de tekst om iets af te leiden.\n\nVraag jezelf: "Wat kan ik hieruit opmaken?"',
        },
        {
          title: 'Let op signaalwoorden',
          content:
            '"toch" → er is een contrast/verrassing\n"dus" → er volgt een conclusie\n"hierdoor" → er is een oorzaak-gevolg\n"maar eigenlijk" → de echte reden volgt',
        },
        {
          title: 'Veelgemaakte fout',
          content:
            'Kies niet een detail uit de tekst als conclusie.\nDe conclusie moet de HELE tekst samenvatten of er iets nieuws uit afleiden.',
        },
      ],
    },
    doel_schrijver: {
      id: 'taalbegrip-doel-schrijver',
      title: '🎯 Doel van de Schrijver',
      description: 'Wat wil de schrijver duidelijk maken?',
      steps: [
        {
          title: 'Strategie',
          content:
            'Vraag: "Waarom heeft de schrijver deze tekst geschreven?"\nMogelijke doelen:\n- Informeren (feiten vertellen)\n- Overtuigen (mening geven, oproep tot actie)\n- Vermaken (leuk verhaal)\n- Instrueren (uitleggen hoe iets moet)\n- Waarschuwen (gevaar melden)',
        },
        {
          title: 'Signalen',
          content:
            'Overtuigen: "zou moeten", "het is belangrijk dat", vergelijkingen\nInformeren: cijfers, feiten, bronnen\nWaarschuwen: "let op", "gevaarlijk", "blijf binnen"',
        },
      ],
    },
    tekststructuur: {
      id: 'taalbegrip-tekststructuur',
      title: '🏗️ Tekststructuur',
      description: 'Hoe is de tekst opgebouwd?',
      steps: [
        {
          title: 'Veelvoorkomende structuren',
          content:
            '• Tijdsvolgorde: verleden → heden → toekomst\n• Oorzaak-gevolg: iets gebeurt → daardoor gebeurt iets anders\n• Probleem-oplossing: er is een probleem → zo los je het op\n• Voor- en nadelen: enerzijds... anderzijds\n• Vergelijking: twee dingen worden vergeleken\n• Misverstand weerleggen: fout idee → de waarheid',
        },
        {
          title: 'Hoe herken je het?',
          content:
            'Let op signaalwoorden!\n"Eerst... daarna... ten slotte" → tijdsvolgorde\n"Hierdoor... daardoor" → oorzaak-gevolg\n"Enerzijds... anderzijds" → voor- en nadelen\n"Hoewel... toch" → tegenstelling/weerlegging',
        },
      ],
    },
    tekstsoort: {
      id: 'taalbegrip-tekstsoort',
      title: '📄 Tekstsoort',
      description: 'Wat voor soort tekst is het?',
      steps: [
        {
          title: 'Soorten teksten',
          content:
            '• Informatief: geeft feiten en uitleg\n• Betogend/overtuigend: wil je mening veranderen\n• Verhalend: vertelt een verhaal\n• Instructie: legt uit hoe iets moet\n• Wervend (reclame): wil je iets laten kopen',
        },
        {
          title: 'Hoe herken je het?',
          content:
            'Informatief: feiten, cijfers, neutrale toon\nBetogend: "ik vind", "zou moeten", emotionele woorden\nWervend: "NIEUW!", korting, overdrijving, "Op=Op!"\nInstructie: stappen, gebiedende wijs ("Meng...", "Bak...")',
        },
      ],
    },

    // ── VERBANDEN (Verbanden leggen in tekst) ──
    verwijswoorden: {
      id: 'taalbegrip-verwijswoorden',
      title: '👉 Verwijswoorden',
      description: 'Waar verwijst "dit", "dat", "hierdoor" naar?',
      steps: [
        {
          title: 'Strategie',
          content:
            'Een verwijswoord verwijst naar iets dat eerder in de tekst staat.\nLees de zin ervoor om te vinden waar het naar verwijst.',
        },
        {
          title: 'Veelvoorkomende verwijswoorden',
          content:
            'dit/dat → verwijst naar een feit of bewering\nhierdoor/daardoor → verwijst naar een oorzaak\nhij/zij/het/ze → verwijst naar een persoon of ding\ner → verwijst naar een locatie\ndarvan/ervan → verwijst naar een groep of onderwerp\nsindsdien → verwijst naar een tijdstip\ndaarin → verwijst naar een activiteit of doel',
        },
        {
          title: 'Tip',
          content:
            'Kijk altijd naar de zin ERVOOR. Het verwijswoord verwijst bijna altijd naar iets in de vorige zin.',
        },
      ],
    },
    signaalwoorden: {
      id: 'taalbegrip-signaalwoorden',
      title: '🚦 Signaalwoorden',
      description: 'Woorden die het verband aangeven.',
      steps: [
        {
          title: 'Tegenstelling',
          content:
            'maar, toch, hoewel, desondanks, echter\n→ Er komt iets onverwachts',
        },
        {
          title: 'Oorzaak-gevolg',
          content:
            'daarom, dus, hierdoor, doordat, daardoor, omdat, want\n→ Het één leidt tot het ander',
        },
        {
          title: 'Opsomming',
          content:
            'bovendien, daarnaast, ook, niet alleen... maar ook\n→ Er komt iets bij',
        },
        {
          title: 'Tijdsvolgorde',
          content:
            'eerst, daarna, vervolgens, uiteindelijk, ten slotte, tegenwoordig\n→ De volgorde van gebeurtenissen',
        },
        {
          title: 'Twee kanten',
          content:
            'enerzijds... anderzijds\n→ Twee kanten van een zaak (voor- en nadeel)',
        },
      ],
    },
    oorzaak_gevolg: {
      id: 'taalbegrip-oorzaak-gevolg',
      title: '🔗 Oorzaak en Gevolg',
      description: 'Waarom gebeurt iets? Wat is het gevolg?',
      steps: [
        {
          title: 'Strategie',
          content:
            'Zoek het verband: wat is de OORZAAK en wat is het GEVOLG?\nDe oorzaak komt vaak eerst, het gevolg daarna.',
        },
        {
          title: 'Signaalwoorden',
          content:
            '"daarom" → het gevolg volgt\n"doordat" → de oorzaak volgt\n"hierdoor" → verwijst naar de oorzaak\n"want" → de reden volgt\n"dus" → de conclusie/het gevolg volgt',
        },
        {
          title: 'Voorbeeld',
          content:
            '"Bamboe geeft weinig energie. Daarom beweegt de panda zo min mogelijk."\nOorzaak: weinig energie\nGevolg: weinig bewegen',
        },
      ],
    },

    // ── KRITISCH LEZEN (Kritisch lezen en beoordelen) ──
    feit_mening: {
      id: 'taalbegrip-feit-mening',
      title: '⚖️ Feit of Mening?',
      description: 'Het verschil tussen feiten en meningen herkennen.',
      steps: [
        {
          title: 'Feit',
          content:
            'Een feit is controleerbaar en meetbaar.\nSignalen: cijfers, onderzoek, "uit onderzoek blijkt"\n\nVoorbeeld: "Nederland heeft 17,5 miljoen inwoners." → feit (te controleren)',
        },
        {
          title: 'Mening',
          content:
            'Een mening is wat iemand vindt of gelooft.\nSignalen: "ik vind", "zou moeten", "volgens mij", "het beste", "belachelijk"\n\nVoorbeeld: "Iedereen zou moeten sporten." → mening',
        },
        {
          title: 'Objectief vs. Subjectief',
          content:
            'Objectief = gebaseerd op feiten, zonder eigen mening\nSubjectief = gebaseerd op persoonlijke gevoelens\n\nTip: Kan iemand het er NIET mee eens zijn? Dan is het een mening.',
        },
      ],
    },
    perspectief: {
      id: 'taalbegrip-perspectief',
      title: '👁️ Perspectief Herkennen',
      description: 'Vanuit welk standpunt is de tekst geschreven?',
      steps: [
        {
          title: 'Hoe herken je het?',
          content:
            'Let op woorden als:\n"Wij vinden..." → de schrijver is VOOR iets\n"Daar zijn we het niet mee eens" → de schrijver is TEGEN iets\n"Volgens experts..." → de schrijver verwijst naar anderen',
        },
        {
          title: 'Vragen om te stellen',
          content:
            '1. Is de schrijver vóór of tegen het onderwerp?\n2. Schrijft de schrijver neutraal of neemt hij een standpunt in?\n3. Voor wie is de tekst bedoeld?',
        },
      ],
    },
    samenvatten: {
      id: 'taalbegrip-samenvatten',
      title: '📝 Samenvatten',
      description: 'De kern van een tekst in eigen woorden.',
      steps: [
        {
          title: 'Stappenplan',
          content:
            'Stap 1: Lees de hele tekst\nStap 2: Zoek de hoofdgedachte (waar gaat het over?)\nStap 3: Zoek de belangrijkste punten (max 3)\nStap 4: Zet het in je eigen woorden',
        },
        {
          title: 'Wat hoort er NIET in?',
          content:
            'Geen details of voorbeelden\nGeen eigen mening\nGeen informatie die niet in de tekst staat\n\nEen goede samenvatting dekt ALLE belangrijke punten.',
        },
      ],
    },
    argumentatie: {
      id: 'taalbegrip-argumentatie',
      title: '💬 Argumentatie',
      description: 'Argumenten herkennen en beoordelen.',
      steps: [
        {
          title: 'Wat is een argument?',
          content:
            'Een argument is een reden die je geeft om je mening te ondersteunen.\nEen goed argument is logisch en gebaseerd op feiten.',
        },
        {
          title: 'Drogreden',
          content:
            'Een drogreden is een argument dat niet klopt, maar wel zo klinkt.\nVoorbeeld: "Iedereen doet het, dus het is goed." → dat klopt niet per se!',
        },
      ],
    },

    // ── STIJL (Stijlfiguren herkennen) ──
    stijlfiguren: {
      id: 'taalbegrip-stijlfiguren',
      title: '🎭 Stijlfiguren',
      description: 'Bijzonder taalgebruik herkennen.',
      steps: [
        {
          title: 'Retorische vraag',
          content:
            'Een vraag waarop je geen antwoord verwacht.\n"Wie wil er nu niet gelukkig zijn?"',
        },
        {
          title: 'Figuurlijk taalgebruik',
          content:
            'Niet letterlijk bedoeld.\n"Het regent pijpenstelen" = het regent heel hard\n"Met de deur in huis vallen" = meteen ter zake komen',
        },
        {
          title: 'Ironie & Sarcasme',
          content:
            'Ironie = milde spot, het omgekeerde zeggen van wat je bedoelt\nSarcasme = bijtende, harde spot',
        },
        {
          title: 'Andere stijlfiguren',
          content:
            'Eufemisme = verzachtende uitdrukking ("heengegaan" i.p.v. "gestorven")\nTautologie = twee keer hetzelfde zeggen ("gratis en voor niets")\nParadox = schijnbare tegenstelling\nPleonasme = dubbelop ("witte sneeuw")',
        },
      ],
    },

    // === Legacy hints (voor oude subSkills die nog in sommige vragen staan) ===
    stoffelijk_bn: {
      id: 'taalbegrip-stoffelijk-bn',
      title: '🪵 Stoffelijk Bijvoeglijk Naamwoord',
      description: 'Van welke stof is het gemaakt?',
      steps: [
        {
          content:
            'Een stoffelijk bijvoeglijk naamwoord vertelt van welke stof iets gemaakt is.\nhout → houten\ngoud → gouden\nsteen → stenen',
        },
      ],
    },
    bijvoeglijk_nw: {
      id: 'taalbegrip-bijvoeglijk-nw',
      title: '🎨 Bijvoeglijk Naamwoord',
      description: 'Hoe is iets?',
      steps: [
        {
          content:
            'Een bijvoeglijk naamwoord vertelt iets over een zelfstandig naamwoord.\nHet zegt HOE iets is: groot, klein, mooi, rood, oud, nieuw.',
        },
      ],
    },
    zelfstandig_nw: {
      id: 'taalbegrip-zelfstandig-nw',
      title: '📦 Zelfstandig Naamwoord',
      description: 'Mens, dier of ding.',
      steps: [
        {
          content:
            'Een zelfstandig naamwoord is een mens, dier of ding.\nJe kunt er "de" of "het" voor zetten.',
        },
      ],
    },
    werkwoord: {
      id: 'taalbegrip-werkwoord',
      title: '🏃 Werkwoord (Doe-woord)',
      description: 'Wat doe je?',
      steps: [
        {
          content:
            'Een werkwoord vertelt WAT je doet.\nVoorbeelden: lopen, rennen, zwemmen, eten, slapen, zijn, hebben.',
        },
      ],
    },
    lidwoord: {
      id: 'taalbegrip-lidwoord',
      title: '📎 Lidwoorden',
      description: 'De, het of een.',
      steps: [
        {
          content: 'Bepaalde lidwoorden: de, het\nOnbepaald lidwoord: een',
        },
      ],
    },
    figuurlijk: {
      id: 'taalbegrip-figuurlijk',
      title: '🎭 Figuurlijk Taalgebruik',
      description: 'Niet letterlijk bedoeld.',
      steps: [
        {
          content:
            'Figuurlijk taalgebruik betekent dat je iets NIET letterlijk bedoelt.\n"Het regent pijpenstelen" → het regent heel hard',
        },
      ],
    },
    // Legacy: old subSkill names that may still be referenced
    woordenschat: {
      id: 'taalbegrip-woordenschat-legacy',
      title: '📚 Woordenschat',
      description: 'Moeilijke woorden begrijpen.',
      steps: [
        {
          title: 'Strategie',
          content:
            'Ken je het woord niet? Kijk naar:\n1. De context (de zinnen eromheen)\n2. Woorddelen (on- = niet, -baar = in staat om)\n3. Vergelijkbare woorden die je wél kent',
        },
      ],
    },
  },

  // Studievaardig and Topo remain empty (coming soon modules)
  Studievaardig: {},
  Topo: {},
}

/**
 * Get hints for a specific module and subskill.
 * Falls back to partial match, then first available hint.
 */
export function getHintsForSubSkill(
  moduleId: ModuleId,
  subSkill?: string,
): SubSkillHint | null {
  const moduleHints = HINT_REGISTRY[moduleId]
  if (!moduleHints) return null

  // Direct match
  if (subSkill && moduleHints[subSkill]) {
    return moduleHints[subSkill]
  }

  // Partial match: check if any registry key is contained in the subSkill
  if (subSkill) {
    const key = Object.keys(moduleHints).find(
      (k) => subSkill.includes(k) || k.includes(subSkill),
    )
    if (key) return moduleHints[key]
  }

  // Fallback: return first available hint
  const keys = Object.keys(moduleHints)
  if (keys.length > 0) {
    return moduleHints[keys[0]]
  }

  return null
}

/**
 * Get all hints for a module (for the overview panel)
 */
export function getAllHintsForModule(moduleId: ModuleId): SubSkillHint[] {
  const moduleHints = HINT_REGISTRY[moduleId]
  if (!moduleHints) return []
  return Object.values(moduleHints)
}

/**
 * Get all subSkill keys that have hints for a module
 */
export function getAllSubSkillsWithHints(moduleId: ModuleId): string[] {
  const moduleHints = HINT_REGISTRY[moduleId]
  if (!moduleHints) return []
  return Object.keys(moduleHints)
}
